<script lang="ts">
  /**
   * The centred `h2` + lead paragraph that opens almost every section.
   *
   * The scale is now fixed here rather than chosen per call site. Across the
   * site the same role carried six different bottom margins — `mb-3` on the
   * keyword pages, `mb-4` seven times on Intelligence, `mb-6`, `mb-8`,
   * `mb-16`, and three headings with none at all, their lead paragraph
   * supplying `mt-2` or `mt-6` instead. None of that was a decision.
   *
   *   heading -> lead      mb-3
   *   block   -> content   mb-10
   *   lead measure         max-w-3xl, centred
   *
   * The props remain so a genuinely different section can say so, but they
   * are no longer how the common case gets its spacing. Changing the scale
   * for the whole site is now an edit to these three defaults.
   */
  let {
    heading,
    lead = '',
    /**
     * An uppercase label above the heading.
     *
     * The hero's own device — `.hero-badge` is uppercase, letter-spaced and
     * accent-tinted, and it appeared nowhere below the fold, which is much of
     * why the hero read as designed and the sections read as a template. On a
     * dark section it renders as that exact class; on a light one as
     * `.t-eyebrow`, the same idea in the brand colour.
     *
     * Empty by default, so no existing page changes until it is passed.
     */
    eyebrow = '',
    /** `lg` is the section default; `md` is the smaller "Verwandte Themen" variant. */
    size = 'lg',
    /** Space below the whole block. */
    spacing = 'mb-10',
    /** Space between heading and lead, as a class on whichever element carried it. */
    gap = 'mb-3',
    /** Extra classes for the lead. The default constrains the measure. */
    leadClass = 'mx-auto max-w-3xl leading-relaxed',
    /** Flip the text colours for a section on the brand surface. */
    inverse = false,
    /** Left-align instead of centring. Splits and lists read better ranged left. */
    align = 'center'
  }: {
    heading: string;
    lead?: string;
    eyebrow?: string;
    size?: 'lg' | 'md';
    spacing?: string;
    gap?: string;
    leadClass?: string;
    inverse?: boolean;
    align?: 'center' | 'left';
  } = $props();

  const headingSize = $derived(size === 'lg' ? 't-heading' : 't-heading-sm');
  /**
   * `!` rather than a second role class.
   *
   * `.t-heading-inverse` exists but carries its own `mb-6`, which would fight
   * the `gap` prop. An important colour override keeps one size role and one
   * margin source.
   */
  const headTone = $derived(inverse ? '!text-ink-inverse' : '');
  const leadTone = $derived(inverse ? '!text-ink-inverse-muted' : '');
</script>

<div class="{align === 'center' ? 'text-center' : ''} {spacing}">
  {#if eyebrow}
    <p class="{inverse ? 'hero-badge' : 't-eyebrow'} mb-2">{eyebrow}</p>
  {/if}
  <h2 class="{headingSize} {headTone} {lead ? gap : ''}">
    {heading}
  </h2>
  {#if lead}
    <p class="t-lead {leadTone} {align === 'center' ? leadClass : 'max-w-2xl leading-relaxed'}">
      {lead}
    </p>
  {/if}
</div>
